/**
 * Team & Access (`/users`): who can sign in, and what each role may do.
 *
 * The house list layout: one white card whose first row is a tab bar, then
 * the open tab. **Users** (`user.view`) manages the accounts; **Permissions**
 * (`access.permissions.view`) shows every action and which role holds it.
 * Each tab shows to whoever holds its permission; the open one is in `?tab=`,
 * so it links. Custom groups will join as a tab of their own.
 */

import type { ComponentType } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import { ShieldCheck, Users as UsersIcon } from 'lucide-react';

import { usePermissionMatrix, useUsers } from '@/api/queries';
import { useSession } from '@/session/SessionContext';
import { openTab, permitted, type PermissionKey } from '@/session/permissions';
import { PageHead } from '@/ui/PageHead';

import { PermissionsTab, actionCount } from './PermissionsTab';
import { UsersTab } from './UsersTab';

const TEAM_TABS: readonly { key: string; name: string; icon: LucideIcon; Screen: ComponentType; permission: PermissionKey }[] = [
  { key: 'users', name: 'Users', icon: UsersIcon, Screen: UsersTab, permission: 'user.view' },
  { key: 'permissions', name: 'Permissions', icon: ShieldCheck, Screen: PermissionsTab, permission: 'access.permissions.view' },
];

export function UsersPage() {
  const { permissions, can } = useSession();
  const [params, setParams] = useSearchParams();
  const tabs = permitted(permissions, TEAM_TABS);
  const open = openTab(tabs, params.get('tab'));
  // The counts on the tabs; each tab reads the same queries.
  const users = useUsers({ enabled: can('user.view') });
  const matrix = usePermissionMatrix({ enabled: can('access.permissions.view') });
  const counts: Record<string, number | undefined> = {
    users: users.data?.users.length,
    permissions: matrix.data ? actionCount(matrix.data) : undefined,
  };

  return (
    <>
      <PageHead title="Team & Access" subtitle="Who can sign in to Wynxx Discover, and what each role may do" />

      <div className="card list-card config-card">
        <div className="tabs card-tabs" role="tablist" aria-label="Team & Access">
          {tabs.map(({ key, name, icon: Icon }) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={open?.key === key}
              className={`tab${open?.key === key ? ' is-active' : ''}`}
              onClick={() => setParams({ tab: key }, { replace: true })}
            >
              <Icon aria-hidden="true" />
              {name}
              {counts[key] !== undefined && <span className="count">{counts[key]}</span>}
            </button>
          ))}
        </div>
        {open && <open.Screen />}
      </div>
    </>
  );
}
